import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
  Button,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ReservationHistory, PagedReservationHistory } from '../../../types/service-types/ReservationTypes';
import PaginationBar from 'app/main/shared-components/pagination-bar/PaginationBar';
import DateUtils from "app/utils/DateUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  cardFooter: {
    opacity: 0.8,
  },
  diffOld: {
    textDecoration: 'line-through',
    opacity: 0.8,
    wordBreak: 'break-word',
  },
  diffNew: {
    fontWeight: 600,
    wordBreak: 'break-word',
  },
  fieldChip: {
    marginRight: theme.spacing(1),
  },
  emptyChip: {
    fontSize: '0.75rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  noDataContainer: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  paginationBar: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));

interface ReservationHistoryComponentProps {
  reservationId: string;
  fetchReservationHistory: (id: string, page?: number, size?: number) => Promise<PagedReservationHistory>;
}

const ReservationHistoryComponent: React.FC<ReservationHistoryComponentProps> = ({
  reservationId,
  fetchReservationHistory,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [historyData, setHistoryData] = useState<PagedReservationHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const loadHistoryData = async (currentPage: number, pageSize: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchReservationHistory(reservationId, currentPage, pageSize);
      setHistoryData(data);
    } catch (err) {
      setError(t('app.reservation.history.error.loadFailed'));
      console.error('Error fetching reservation history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistoryData(page, rowsPerPage);
  }, [reservationId, page]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const getFieldDisplayName = (fieldName: string) => {
    const key = `app.reservation.history.fields.${fieldName}`;
    const translation = t(key);
    return translation !== key ? translation : fieldName;
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.noDataContainer}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!historyData || historyData.content.length === 0) {
    return (
      <Box className={classes.noDataContainer}>
        <Typography variant="body1" color="textSecondary">
          {t('app.reservation.history.noData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>

      {historyData.content.map((row) => {
        const changedAtHuman = row.changedAt
        ? DateUtils.toHumanDateWithHour(row.changedAt)
        : "-";
        const isExpanded = expandedRow === row.id;

        return (
          <Card key={row.id} className={classes.card}>
            <CardContent>
              <Typography className={classes.title} color="textSecondary" gutterBottom>
                {t('app.main.schedules.history.changedOn', 'Changed on')}:
              </Typography>
              <Typography variant="h5" component="h2">
                {changedAtHuman}
              </Typography>
              <br />
              <TableContainer component={Paper}>
                <Table aria-label="history table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('app.reservation.history.table.fieldName')}</TableCell>
                      <TableCell>{t('app.reservation.history.table.oldValue')}</TableCell>
                      <TableCell>{t('app.reservation.history.table.newValue')}</TableCell>
                      <TableCell>{t('app.reservation.history.table.changedBy')}</TableCell>
                      <TableCell>{t('app.reservation.history.table.changedAt')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Chip
                          label={getFieldDisplayName(row.fieldName)}
                          size="small"
                          className={classes.fieldChip}
                        />
                      </TableCell>
                      <TableCell>
                        <span className={classes.diffOld}>{row.oldValue ?? '—'}</span>
                      </TableCell>
                      <TableCell>
                        <span className={classes.diffNew}>{row.newValue ?? '—'}</span>
                      </TableCell>
                      <TableCell>{row.changedBy || '—'}</TableCell>
                      <TableCell>{changedAtHuman}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>

            <CardActions>
              {isExpanded ? (
                <Typography variant="body2" component="p" className={classes.cardFooter}>
                  {t('app.main.schedules.history.recordId', 'Record ID')}: <strong>{row.id}</strong>
                  <br />
                  {t('app.main.schedules.history.reservationId', 'Reservation ID')}: <strong>{reservationId}</strong>
                </Typography>
              ) : (
                <Button onClick={() => setExpandedRow(isExpanded ? null : row.id)} size="small">
                  {t("app.main.schedules.activities.reservationMore", 'Read more')}
                </Button>
              )}
            </CardActions>
          </Card>
        );
      })}

      <PaginationBar totalPages={historyData.totalPages ?? 0} onPageChange={handlePageChange} />
    </Box>
  );
};

export default ReservationHistoryComponent;
